// ProjectsStats.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TablePagination,
  useTheme,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { IconButton } from "@mui/material";
import Layout from "../Layout";
// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Define MenuProps to fix dropdown x-axis position
const menuProps = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
  PaperProps: {
    style: {
      transformOrigin: "top left",
      left: 0,
    },
  },
};

const ProjectsStats = () => {
  // States for Projects Statistics (chart)
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [orgProjectsData, setOrgProjectsData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [organization, setOrganization] = useState("");
  const theme = useTheme();
  const [loadingDownloadId, setLoadingDownloadId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // State for organizations dropdown (shared across both sections)
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [errorOrgs, setErrorOrgs] = useState(null);

  // States for pagination of the stats table
  const [statsPage, setStatsPage] = useState(0);
  const [statsRowsPerPage, setStatsRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [filteredProjects, setFilteredProjects] = useState([]);
  // States for Projects List section
  const [selectedOrgForProjectsList, setSelectedOrgForProjectsList] =
    useState("all");
  const [projectsList, setProjectsList] = useState([]);
  const [loadingProjectsList, setLoadingProjectsList] = useState(false);
  const [errorProjectsList, setErrorProjectsList] = useState(null);

  // States for pagination of the projects list table
  const [projPage, setProjPage] = useState(0);
  const [projRowsPerPage, setProjRowsPerPage] = useState(5);

  // Fetch organizations for dropdowns
  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoadingOrgs(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/organizations`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch organizations");
        }
        const data = await response.json();
        setOrganizations(data.organizations || []);
      } catch (err) {
        setErrorOrgs(err.message);
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, []);

  const handleDownloadPDF = async (projectId) => {
    setLoadingDownloadId(projectId);

    try {
      const response = await fetch(
        "https://report-api.athrvcloud.com:5006/analyze",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            project_id: projectId,
            download_pdf: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 10000);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setLoadingDownloadId(null);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = projectsList.filter((project) =>
      project.name.toLowerCase().includes(value)
    );
    setFilteredProjects(filtered);
    setProjPage(0); // Reset pagination to page 0
  };

  // Auto-select top 10 organizations (if none selected) for the stats chart
  useEffect(() => {
    if (!selectedOrgIds.length && organizations.length > 0) {
      const topTen = organizations
        .slice(0, 10)
        .map((org) => org.organization_id);
      setSelectedOrgIds(topTen);
    }
  }, [organizations, selectedOrgIds]);

  // Fetch projects statistics data based on selected organization IDs
  useEffect(() => {
    if (selectedOrgIds.length === 0) {
      setOrgProjectsData([]);
      return;
    }
    const fetchStats = async () => {
      setLoadingData(true);
      setErrorData(null);
      try {
        const orgIdsParam = selectedOrgIds.join(",");
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/dashboard_org_projects_status_widget?organization_ids=${orgIdsParam}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch project status stats");
        }
        const data = await response.json();
        // Expecting data.organizations: [{ organization_id, total_projects, completed, failed, in_progress }, ...]
        setOrgProjectsData(data.organizations || []);
      } catch (err) {
        setErrorData(err.message);
      } finally {
        setLoadingData(false);
      }
    };
    fetchStats();
  }, [selectedOrgIds]);

  // Prepare chart data for grouped bar chart
  const chartData = {
    labels: orgProjectsData.map((item) => {
      // Match organization name using organization_id
      const org = organizations.find(
        (o) => o.organization_id === item.organization_id
      );
      return org ? org.name : item.organization_id;
    }),
    datasets: [
      {
        label: "Total Projects",
        data: orgProjectsData.map((item) => item.total_projects),
        backgroundColor: "#1976d2",
      },
      {
        label: "Completed",
        data: orgProjectsData.map((item) => item.completed),
        backgroundColor: "#339e25",
      },
      {
        label: "Failed",
        data: orgProjectsData.map((item) => item.failed),
        backgroundColor: "#e73535",
      },
      {
        label: "In Progress",
        data: orgProjectsData.map((item) => item.in_progress),
        backgroundColor: "#f7903c",
      },
    ],
  };

  // Fetch Projects List based on filter selection (single select: "all" or specific organization)
  useEffect(() => {
    const fetchProjectsList = async () => {
      setLoadingProjectsList(true);
      setErrorProjectsList(null);
      try {
        let url = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects`;
        if (selectedOrgForProjectsList !== "all") {
          url += `?organization_id=${selectedOrgForProjectsList}`;
        }
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch projects list");
        }
        const data = await response.json();
        setProjectsList(data.projects_list || []);
        setOrganization(data);
      } catch (err) {
        setErrorProjectsList(err.message);
      } finally {
        setLoadingProjectsList(false);
      }
    };
    fetchProjectsList();
  }, [selectedOrgForProjectsList]);

  // Pagination handlers for stats table
  const handleStatsChangePage = (event, newPage) => {
    setStatsPage(newPage);
  };

  const handleStatsChangeRowsPerPage = (event) => {
    setStatsRowsPerPage(parseInt(event.target.value, 10));
    setStatsPage(0);
  };

  // Pagination handlers for projects list table
  const handleProjChangePage = (event, newPage) => {
    setProjPage(newPage);
  };

  const handleProjChangeRowsPerPage = (event) => {
    setProjRowsPerPage(parseInt(event.target.value, 10));
    setProjPage(0);
  };

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#fafafa",
      }}
    >
      {/* Projects Statistics Section */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Projects Statistics by Organization
      </Typography>
      <FormControl sx={{ minWidth: 300, mb: 3 }}>
        <InputLabel id="org-multi-select-label">
          Select Organizations
        </InputLabel>
        <Select
          labelId="org-multi-select-label"
          multiple
          value={selectedOrgIds}
          onChange={(e) => setSelectedOrgIds(e.target.value)}
          input={<OutlinedInput label="Select Organizations" />}
          renderValue={(selected) =>
            selected
              .map((id) => {
                const org = organizations.find((o) => o.organization_id === id);
                return org ? org.name : id;
              })
              .join(", ")
          }
          MenuProps={menuProps}
        >
          {organizations.map((org) => (
            <MenuItem key={org.organization_id} value={org.organization_id}>
              <Checkbox
                checked={selectedOrgIds.indexOf(org.organization_id) > -1}
              />
              <ListItemText primary={org.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loadingData ? (
        <CircularProgress />
      ) : errorData ? (
        <Alert severity="error">{errorData}</Alert>
      ) : orgProjectsData.length > 0 ? (
        <Box sx={{ maxWidth: "100%", height: 400, mb: 3 }}>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { position: "top" } },
            }}
          />
        </Box>
      ) : (
        <Typography>Select Organizations to show Project Statistics</Typography>
      )}

      {/* Detailed Stats Table with Pagination */}
      {orgProjectsData.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Total Projects</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>In Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgProjectsData
                  .slice(
                    statsPage * statsRowsPerPage,
                    statsPage * statsRowsPerPage + statsRowsPerPage
                  )
                  .map((item) => {
                    const org = organizations.find(
                      (o) => o.organization_id === item.organization_id
                    );
                    return (
                      <TableRow key={item.organization_id}>
                        <TableCell>
                          {org ? org.name : item.organization_id}
                        </TableCell>
                        <TableCell>{item.total_projects}</TableCell>
                        <TableCell>{item.completed}</TableCell>
                        <TableCell>{item.failed}</TableCell>
                        <TableCell>{item.in_progress}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={orgProjectsData.length}
            page={statsPage}
            onPageChange={handleStatsChangePage}
            rowsPerPage={statsRowsPerPage}
            onRowsPerPageChange={handleStatsChangeRowsPerPage}
          />
        </>
      )}

      {/* Projects List Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Projects List
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          <FormControl sx={{ minWidth: 300, mb: 3 }}>
            <InputLabel id="org-single-select-label">
              Filter by Organization
            </InputLabel>
            <Select
              labelId="org-single-select-label"
              value={selectedOrgForProjectsList}
              onChange={(e) => setSelectedOrgForProjectsList(e.target.value)}
              input={<OutlinedInput label="Filter by Organization" />}
            >
              <MenuItem value="all">
                <em>All Organizations</em>
              </MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.organization_id} value={org.organization_id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            placeholder="Search Projects"
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loadingProjectsList ? (
          <CircularProgress />
        ) : errorProjectsList ? (
          <Alert severity="error">{errorProjectsList}</Alert>
        ) : projectsList.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Project Name</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Ai Report</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(searchTerm ? filteredProjects : projectsList)
                    .slice(
                      projPage * projRowsPerPage,
                      projPage * projRowsPerPage + projRowsPerPage
                    )
                    .map((project, index) => (
                      <TableRow key={index}>
                        <TableCell>{project.name}</TableCell>
                        <TableCell>
                          {new Date(
                            parseInt(project.createdAt) * 1000
                          ).toLocaleString()}
                        </TableCell>
                        <TableCell>{project.createdBy}</TableCell>
                        <TableCell>
                          {organizations.find(
                            (org) =>
                              org.organization_id === project.organization_id
                          )?.name || "N/A"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              handleDownloadPDF(project.project_id)
                            }
                            disabled={loadingDownloadId === project.project_id}
                          >
                            {loadingDownloadId === project.project_id ? (
                              <CircularProgress size={24} />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={projectsList.length}
              page={projPage}
              onPageChange={handleProjChangePage}
              rowsPerPage={projRowsPerPage}
              onRowsPerPageChange={handleProjChangeRowsPerPage}
            />
          </>
        ) : (
          <Typography>No projects found for the selected filter.</Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ProjectsStats;
